import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

type ShotTarget = { src: string; alt: string };

/**
 * Lightbox para screenshots do produto (.shot).
 * No mobile abre bem ampliado (~260vw) com pan — UI de produto legível.
 */
export function ShotLightbox() {
  const [shot, setShot] = useState<ShotTarget | null>(null);
  const titleId = useId();
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const root = document.querySelector('.opx-site');
    if (!root) return;

    const decorate = () => {
      root.querySelectorAll<HTMLImageElement>('img.shot').forEach((img) => {
        if (img.dataset.zoomReady) return;
        img.dataset.zoomReady = '1';
        img.setAttribute('role', 'button');
        img.tabIndex = 0;
        img.setAttribute('aria-label', `Ampliar: ${img.alt || 'captura do sistema'}`);
        const win = img.closest('.win');
        if (win) win.classList.add('shot-zoomable');
      });
    };
    decorate();
    const mo = new MutationObserver(decorate);
    mo.observe(root, { childList: true, subtree: true });

    const openFrom = (img: HTMLImageElement) => {
      setShot({ src: img.currentSrc || img.src, alt: img.alt || 'Captura do sistema Operax' });
    };

    const onClick = (e: Event) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const img = t.closest('img.shot');
      if (!(img instanceof HTMLImageElement)) return;
      e.preventDefault();
      openFrom(img);
    };

    const onKey = (e: Event) => {
      if (!(e instanceof KeyboardEvent)) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const t = e.target;
      if (!(t instanceof HTMLImageElement) || !t.classList.contains('shot')) return;
      e.preventDefault();
      openFrom(t);
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKey);
    return () => {
      mo.disconnect();
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (!shot) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShot(null);
    };
    window.addEventListener('keydown', onKey);

    // Esconde bubble do webchat enquanto o zoom está aberto (z-index do widget compete)
    const wc = document.getElementById('operax-webchat-root') as HTMLElement | null;
    const prevWc = wc?.style.visibility ?? '';
    if (wc) wc.style.visibility = 'hidden';

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      if (wc) wc.style.visibility = prevWc;
    };
  }, [shot]);

  // Após carregar a imagem: centraliza o pan
  useEffect(() => {
    if (!shot) return;
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img) return;

    const settle = () => {
      const sw = stage.clientWidth;
      const sh = stage.clientHeight;
      const iw = img.getBoundingClientRect().width;
      const ih = img.getBoundingClientRect().height;
      // Foca a área central-direita (chat do produto, não a sidebar)
      stage.scrollLeft = Math.max(0, (iw - sw) * 0.42);
      stage.scrollTop = Math.max(0, (ih - sh) * 0.08);
    };

    if (img.complete && img.naturalWidth > 0) {
      requestAnimationFrame(settle);
    } else {
      img.addEventListener('load', settle, { once: true });
    }
  }, [shot]);

  if (!shot) return null;

  return createPortal(
    <div
      className="shot-lb"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={() => setShot(null)}
    >
      <div className="shot-lb-bar" onClick={(e) => e.stopPropagation()}>
        <p id={titleId} className="shot-lb-title">
          {shot.alt}
        </p>
        <button type="button" className="shot-lb-close" aria-label="Fechar zoom" autoFocus onClick={() => setShot(null)}>
          <X width={22} height={22} strokeWidth={2.2} />
        </button>
      </div>
      <div className="shot-lb-stage" ref={stageRef} onClick={(e) => e.stopPropagation()}>
        <img className="shot-lb-img" ref={imgRef} src={shot.src} alt={shot.alt} draggable={false} />
      </div>
      <p className="shot-lb-hint">Arraste para explorar · pinça para ampliar · toque fora para fechar</p>
    </div>,
    document.body,
  );
}
